import { getSelectionOoxml } from "../../../utils/ooxmlHelper";
import { safeSync } from "../../../utils/wordApiHelper";
import { bookmarkSelection } from "./bookmarkUtils";

export interface SelectionCaptureResult {
  originalText: string;
  surroundingContext: string;
  originalOoxml: string | null;
}

const MAX_CONTEXT_LENGTH = 2000;
const CONTEXT_PARAGRAPH_WINDOW = 3;

const getSurroundingContext = async (
  context: Word.RequestContext,
  selection: Word.Range
): Promise<string> => {
  try {
    const selParagraphs = selection.paragraphs;
    selParagraphs.load("text");
    const bodyParagraphs = context.document.body.paragraphs;
    bodyParagraphs.load("text");
    await context.sync();

    if (selParagraphs.items.length === 0) return "";

    const anchorText = selParagraphs.items[0].text;
    const allParagraphs = bodyParagraphs.items;

    // Phase 1: exact full-text match
    let anchorIndex = allParagraphs.findIndex((p) => p.text === anchorText);

    // Phase 2: longest-common-prefix fallback (handles minor whitespace diffs)
    if (anchorIndex < 0 && anchorText.length > 0) {
      let bestLen = 0;
      allParagraphs.forEach((p, idx) => {
        let len = 0;
        while (len < anchorText.length && len < p.text.length && anchorText[len] === p.text[len]) len++;
        if (len > bestLen) { bestLen = len; anchorIndex = idx; }
      });
      if (bestLen < 10) anchorIndex = -1; // minimum quality gate
    }

    if (anchorIndex < 0) {
      console.warn("[AIEdit] Could not locate anchor paragraph — context omitted");
      return "";
    }

    const startIdx = Math.max(0, anchorIndex - CONTEXT_PARAGRAPH_WINDOW);
    const endIdx = Math.min(allParagraphs.length - 1, anchorIndex + CONTEXT_PARAGRAPH_WINDOW);
    const contextParts: string[] = [];

    for (let idx = startIdx; idx <= endIdx; idx += 1) {
      if (idx === anchorIndex) continue;
      const paragraphText = allParagraphs[idx].text;
      if (typeof paragraphText === "string" && paragraphText.trim()) {
        contextParts.push(paragraphText.trim());
      }
    }

    const merged = contextParts.join("\n\n");
    return merged.length > MAX_CONTEXT_LENGTH
      ? `${merged.substring(0, MAX_CONTEXT_LENGTH)}...`
      : merged;
  } catch (err) {
    console.warn("[AIEdit] Could not gather surrounding context:", err);
    return "";
  }
};

export const captureSelectionForEdit = async (
  context: Word.RequestContext
): Promise<SelectionCaptureResult> => {
  const selection = context.document.getSelection();
  context.load(selection, "text");
  await safeSync(context, "get selected text");

  const originalText = String(selection.text || "");
  await bookmarkSelection(context);

  let originalOoxml: string | null = null;
  try {
    originalOoxml = await getSelectionOoxml(context, selection);
  } catch {
    // Non-critical: fallback to plain-text apply path.
  }

  const surroundingContext = await getSurroundingContext(context, selection);
  return { originalText, surroundingContext, originalOoxml };
};
